import React, { useLayoutEffect, useRef } from 'react';
import { ensureGlassV3 } from './glassV3';
import AeroDotLoader from './AeroDotLoader';

// fill -> the engine's variant. "ink" is the app's solid ink (no refraction,
// still lit, AA guaranteed); "ink-glass" is the website's translucent ink that
// refracts what is behind it; "glass" is white glass (clear dark glass inside
// a [data-theme="dark"] wrapper, handled by the engine).
export const V3_FILLS = new Set(['ink', 'ink-glass', 'glass']);

const SIZES = new Set(['xs', 'sm', 'md', 'lg']);

// The ink colour comes from the --aero-btn token as resolved on the button
// itself, so a dark-theme wrapper gets the light ink of that theme.
function inkFill(el, translucent) {
  const value = getComputedStyle(el).getPropertyValue('--aero-btn').trim();
  if (!translucent || !/^#[0-9a-f]{6}$/i.test(value)) return value;
  const n = parseInt(value.slice(1), 16);
  // The alpha is replaced by the settings' inkA inside the engine.
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, 0.8)`;
}

/**
 * GlassButtonV3: the final Aero glass button (ink and white glass slabs,
 * radius from the glass settings, colour pickup from what is around it).
 * Rendered by GlassButton for fill="ink" | "ink-glass" | "glass".
 *
 * Props: fill, size ('xs' | 'sm' | 'md' | 'lg', default 'md'), loading,
 * block (full width), className, children; everything else goes to <button>.
 */
export default function GlassButtonV3({
  fill = 'ink',
  size = 'md',
  loading = false,
  block = false,
  className = '',
  children,
  disabled,
  // Original-engine props, accepted and ignored so callers can switch fills freely.
  mode, // eslint-disable-line no-unused-vars
  str, // eslint-disable-line no-unused-vars
  thick, // eslint-disable-line no-unused-vars
  ...rest
}) {
  const ref = useRef(null);
  const sizeClass = SIZES.has(size) ? size : 'md';

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const api = ensureGlassV3();
    if (!api) return undefined;
    // Set up explicitly with this button's variant and fill. The button does
    // not carry data-glass-v3 itself, so the engine's page-wide auto-init can
    // never set it up early with the wrong (default) fill.
    api(el, {
      variant: fill === 'glass' ? 'secondary' : 'primary',
      fill: fill === 'glass' ? undefined : inkFill(el, fill === 'ink-glass'),
    });
    return () => api.release(el);
  }, [fill, sizeClass, block, className]);

  // The engine adds its own classes (g3, g3-primary, g3-tint, g3-warped...)
  // to the element, so React must never rewrite className after mount: it is
  // built only from props that remount the button (see key), and changing
  // state (loading) is a data attribute instead of a class.
  const classes = [
    'aero-g3',
    sizeClass,
    `aero-g3-${fill}`,
    block ? 'aero-g3-block' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <button
      ref={ref}
      // A new element (and a fresh engine setup) when any class-bearing prop changes.
      key={classes}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      data-loading={loading ? '' : undefined}
      {...rest}
    >
      <span className="aero-g3-label">
        {loading && <AeroDotLoader size="sm" />}
        {children}
      </span>
    </button>
  );
}
